import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Pay360SessionState, Pay360TransactionStates } from '../../../constants';
import Pay360SessionIframe from './Pay360SessionIframe';
import Pay360SessionDetails from './Pay360SessionDetails';
import { selectPay360 } from '../../pay360/pay360Slice';
import { selectConfig } from '../../config/configSlice';
import pay360Service from '../../../services/pay360';

function Pay360Session({
  src,
  order,
  amount,
  paymentProcessingFailed,
  sessionState,
  transactionState,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { session } = useSelector(selectPay360);
  const config = useSelector(selectConfig);

  const pay360Errors = {
    FAILED:
      'Sorry, your transaction has been declined by your bank, please contact your bank for more information',
    CANCELLED: 'Purchase cancelled by customer',
    INVALID: 'No result returned',
  };

  const hasSessionExpired = false;
  const isSessionAlive = !hasSessionExpired && sessionState === Pay360SessionState.READY;

  const paymentFailed =
    sessionState === Pay360TransactionStates.TERMINATED && pay360Errors[transactionState];

  const paymentCancelled =
    sessionState === Pay360TransactionStates.TERMINATED && transactionState === 'NOT_SUBMITTED';

  const onComplete = () => {
    if (paymentCancelled) {
      dispatch({ type: 'pay360/reset' });
    } else {
      dispatch({ type: 'pay360/paymentProcessing' });
      dispatch({ type: 'pay360/getSession', payload: { navigate } });
    }
  };

  // Before starting a new Pay360 session, check whether the original transaction
  // completed or is still in flight at Pay360. If it succeeded, trigger the normal
  // completion flow. If it is still processing, poll for the result rather than
  // creating a second session. The ref prevents concurrent retries from a double-tap.
  const retryInFlight = React.useRef(false);
  const handleRetry = React.useCallback(async () => {
    if (retryInFlight.current) return;
    retryInFlight.current = true;
    try {
      try {
        const sessionId = session?.sessionId;
        if (sessionId) {
          const result = await pay360Service.getSession(sessionId, config);
          const { hostedSessionStatus } = result;
          const currentSessionState = hostedSessionStatus?.sessionState;
          const currentTransactionState = hostedSessionStatus?.transactionState?.transactionState;

          // Original transaction already succeeded — route to confirmation
          if (
            currentSessionState === Pay360TransactionStates.TERMINATED &&
            currentTransactionState === Pay360TransactionStates.SUCCESS
          ) {
            dispatch({ type: 'pay360/paymentProcessing' });
            dispatch({ type: 'pay360/getSession', payload: { navigate } });
            return;
          }

          // Original transaction still in flight — poll for the result instead of
          // creating a new session, which would risk a double-charge
          const isStillProcessing =
            currentSessionState !== Pay360TransactionStates.TERMINATED ||
            currentTransactionState === Pay360TransactionStates.PROCESSING ||
            currentTransactionState === Pay360TransactionStates.STARTED;

          if (isStillProcessing) {
            dispatch({ type: 'pay360/paymentProcessing' });
            dispatch({ type: 'pay360/getSession', payload: { navigate } });
            return;
          }
        }
      } catch (_e) {
        // NOSONAR - session check failure is intentional fall-through to createSession
      }
      dispatch({ type: 'pay360/createSession', payload: { amount } });
    } finally {
      retryInFlight.current = false;
    }
  }, [session, config, dispatch, navigate, amount]);

  return isSessionAlive ? (
    <Pay360SessionIframe src={src} onComplete={onComplete} paymentCancelled={paymentCancelled} />
  ) : (
    <Pay360SessionDetails
      state={sessionState}
      transactionState={transactionState}
      order={order}
      hasExpired={hasSessionExpired}
      processingFailed={paymentProcessingFailed}
      paymentFailed={paymentFailed}
      onRetry={handleRetry}
    />
  );
}

Pay360Session.propTypes = {
  src: PropTypes.string,
  order: PropTypes.object,
  amount: PropTypes.number,
  paymentProcessingFailed: PropTypes.bool,
  sessionState: PropTypes.string,
  transactionState: PropTypes.string,
};

export default Pay360Session;
